/**
 * `AuthContext` — the SPA's single source of truth for "who am I".
 *
 * Two backing modes:
 *
 *   - **Clerk mode** (Stream C): when `VITE_CLERK_PUBLISHABLE_KEY` is
 *     set, `<App/>` wraps everything in `<ClerkProvider>` and this
 *     context reads `useAuth().getToken()` to mint a real JWT for
 *     the API. The dev-shim helpers are unused.
 *
 *   - **Dev-shim mode** (Stream A fallback): no Clerk key → the
 *     `?dev=<account_id>` query / localStorage flow from `./auth.ts`
 *     is the source of identity. Used in local development and on
 *     preview deploys where Clerk isn't configured yet.
 *
 *   - **Demo phone mode** (#2824): on `/demo-phone` - the frame inside the demo
 *     roster page's phone - a read-only `demo-member:` token for the picked
 *     staff member (see `DemoMemberAuthProvider`).
 *
 *   - **Telegram mode** (#2074): on `/tg` — the Mini App inside
 *     Telegram — Telegram's signed `initData` buys a `tgapp` session
 *     token (`telegram/session.ts`, memory only) and every request
 *     sends `Bearer tgapp:<token>`. Clerk is never consulted there.
 *
 * Consumers (HomePage, TeamPage, etc.) call `useAuth()` and don't
 * need to know which mode is active.
 */
import { useAuth as useClerkAuth, useSessionList } from "@clerk/clerk-react";
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";

import { ApiError, apiFetch } from "../api/client";
import type { paths } from "../api/schema.gen";
import { DemoEndedScreen } from "../components/demo/DemoEndedScreen";
import {
  type MiniAppSessionProblem,
  setTgappToken,
  startMiniAppSession,
  tgappAuthValue,
} from "../telegram/session";
import {
  isMiniAppPath,
  loadTelegramSdk,
  readInitData,
  startTelegramChrome,
  whenTelegramWebApp,
} from "../telegram/telegramSdk";
import { clearAccountCaches } from "./accountCaches";
import {
  CHOOSE_ACCOUNT_PATH,
  type SignOutScope,
  type SignedInAccount,
  activateDevAccount,
  announceAccountSwitch,
  clearDevRecentAccounts,
  followAccountSwitch,
  forgetAccountNames,
  forgetDevRecentAccount,
  isAccountSwitching,
  noteDevRecentAccount,
  readAccountNames,
  readDevRecentAccounts,
  rememberAccountName,
  runAccountSwitch,
  takeAccountAddPending,
} from "./accountSwitch";
import { setActiveWorkspaceId } from "./activeWorkspace";
import {
  authHeaders,
  clearDevAccountId,
  isDemoPath,
  readDevAccountId,
  setDevAccountId,
  subscribeDevAccountId,
} from "./auth";
import { hasClerk } from "./clerk";
import { isDemoMode } from "./demo";
import { useDemoEnded } from "./demoEnded";
import {
  DEMO_PHONE_MEMBER_PARAM,
  type MemberViewSession,
  demoMemberAuthValue,
  inDemoPhoneFrame,
  mintMemberView,
} from "./demoPhone";
import {
  clearDemoEnded,
  clearDemoSession,
  demoSessionAuthValue,
  endDemoSession,
  readDemoSession,
  setDemoTab,
} from "./demoSession";
import { setTokenSource } from "./tokenSource";

export type { SignedInAccount, SignOutScope } from "./accountSwitch";

type AccountMe = paths["/api/account/me"]["get"]["responses"]["200"]["content"]["application/json"];

interface AuthContextValue {
  /** Backend account id once `/api/account/me` resolves. Null while loading or if signed-out. */
  accountId: string | null;
  account: AccountMe | null;
  loading: boolean;
  error: string | null;
  /** Dev-shim only — ignored in Clerk mode. */
  signIn: (id: string) => void;
  /** Sign out and revoke the session. **Await this before navigating.**
   *  In Clerk mode it is a network round-trip to the Frontend API; tearing
   *  the page down first aborts that request and the session survives the
   *  reload (#1520). The `Promise` return is load-bearing — it is what makes
   *  the await possible, so don't narrow it back to `void`.
   *  `redirectUrl` is where Clerk sends the browser once the revoke lands;
   *  the dev-shim has nothing to revoke and leaves navigation to the caller.
   *  #3005 — `scope` (default `current`): `current` ends this account's
   *  session only (Clerk `signOut({ sessionId })`), `all` every account
   *  signed in on this browser. With one account the two are the same. */
  signOut: (opts?: { redirectUrl?: string; scope?: SignOutScope }) => Promise<void>;
  /** #3005 — the accounts signed in on this browser, current first: Clerk's
   *  signed-in sessions (D1 a), or the dev ids this browser used (dev
   *  builds). Unset in a demo session and in Telegram mode, where the
   *  Switch accounts group does not show. */
  accounts?: SignedInAccount[];
  /** #3005 — switch to another signed-in account by its `key`: cover,
   *  clear the caches, activate, full load of `/` (`lib/accountSwitch.ts`). */
  switchAccount?: (key: string) => Promise<void>;
  /** Re-fetch `/api/account/me`. Pass `{silent: true}` to skip the
   *  global `loading` flag so RequireAuth doesn't unmount its outlet
   *  while a child component (e.g. SolvePage) is mid-flow. */
  refresh: (opts?: { silent?: boolean }) => Promise<void>;
  /** Build the Authorization header for an outbound request. Async because
   *  Clerk may need to mint a fresh JWT. Dev-shim returns synchronously
   *  but we keep the same async shape to avoid two call-paths. */
  authHeader: () => Promise<Record<string, string>>;
  /** Which inner provider is active: Clerk, the dev shim, an
   *  ephemeral `/demo` session (#1753), or the Telegram Mini App (#2074). */
  mode: "clerk" | "dev-shim" | "demo" | "demo-member" | "telegram";
  /** Clerk mode (#2240): the browser holds a live Clerk session but
   *  `/api/account/me` could not be loaded, even after retries (the API
   *  was unreachable, or refused a token minted after a long idle). The
   *  person is NOT signed out — a gate that sends them to `/sign-in`
   *  loses the address they opened and strands them on "You're already
   *  signed in". Gates show a try-again state in place instead. The other
   *  providers leave it unset. */
  sessionWithoutAccount?: boolean;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  // Demo flow: when a dev-shim id is in localStorage AND the visitor
  // is on the `/demo` family of routes, use the dev-shim provider
  // regardless of Clerk being configured. Without the path gate, a
  // leftover dev-shim id from an earlier `/demo` visit (or a stray
  // `?dev=<uuid>` link) would let an unauthenticated visitor land on
  // the marketing root and be auto-signed in as the demo lead persona
  // — issue 002 (`docs/issues/002-incognito-auto-shims-demo-persona.md`).
  // The dev-shim Bearer is a legitimate identity ONLY on `/demo`; on
  // every other surface the session must come from Clerk (or the user
  // sees the landing page / `/sign-in`).
  //
  // We read the path from `window.location` rather than via
  // `useLocation` so this lives at the AuthProvider level (above the
  // BrowserRouter outlet tree). The trade-off: a router-only
  // navigation between `/demo` and `/` does not re-evaluate which
  // provider is mounted. The `/demo` picker (`DemoPage.pickPersona`)
  // and the AppShell user-pill switchers already do a hard
  // `window.location.assign("/")` after writing the dev id — that
  // reload is what re-mounts the AuthProvider with the new path,
  // which is intentional and remains correct after this change.
  //
  // Issue 007 (`docs/issues/007-path-gate-bypassed-when-no-clerk.md`):
  // the original gate (`if (hasClerk && !devShimActive) → Clerk; else
  // DevShim`) only enforced the `onDemoPath` requirement when Clerk
  // was configured. In local dev (no `VITE_CLERK_PUBLISHABLE_KEY`),
  // the conditional fell through to `DevShimAuthProvider` regardless
  // of path — re-introducing the issue 002 symptom on every local
  // surface. The branch order below makes the path-gate the OUTER
  // condition: dev-shim only mounts when both the id is present AND
  // the visitor is on `/demo*`, regardless of `hasClerk`. The two
  // remaining branches are the production Clerk path and the local-
  // dev fallback (a `DevShimAuthProvider` whose mount-time
  // `readDevAccountId()` resolves to `null` on non-`/demo` paths,
  // which leaves the visitor unauthenticated and lets
  // `RequireAuthOrLanding` paint the LandingPage / bounce to /sign-in).
  //
  // Issue 024 (`docs/issues/024-demo-scenario-picker-bounces-back-to-signup.md`):
  // the strict `/demo*` gate broke the demo picker's post-pick contract.
  // The picker writes `localStorage.dev_account_id` then does
  // `window.location.assign('/')` so the AuthProvider re-mounts in
  // dev-shim mode. Post-Brief-27 the AuthProvider mounts on `/`, the
  // strict gate refuses to activate dev-shim, and the visitor is bounced
  // to LandingPage — looping the demo flow back to its own entry. The
  // Option-A widening: dev-shim also activates when sessionStorage holds
  // `tarii.dev_shim_session_authorized === '1'`, the flag the picker
  // writes alongside the dev id. sessionStorage is tab-scoped + cleared
  // on tab close, so it differentiates "deliberate-this-session" from
  // "stale-localStorage-from-old-visit" (the latter still hits
  // LandingPage — issue 002 closure preserved).
  const devShimIdPresent = typeof window !== "undefined" && readDevAccountId() != null;
  const onDemoPath = typeof window !== "undefined" && isDemoPath(window.location.pathname);
  const sessionAuthorized =
    typeof window !== "undefined" &&
    window.sessionStorage.getItem("tarii.dev_shim_session_authorized") === "1";
  const sessionEnablesDevShim = onDemoPath || sessionAuthorized;
  // #1753 — a live ephemeral demo session (minted on `/demo`) wins over
  // every other provider. Unlike the dev shim there is NO path gate: the
  // stored token is a real server-verified credential that can only
  // reach its own ephemeral team, and `readDemoSession()` self-expires,
  // so a stale entry can never re-animate a session the backend would
  // accept. It sits ABOVE the dev-shim branch so the persona picker at
  // `/demo/scenarios` still works after clearing the demo session.
  const demoSessionActive = typeof window !== "undefined" && readDemoSession() != null;
  // #2074 — the Telegram Mini App wins on its own address: inside
  // Telegram the identity is Telegram's signed `initData`, never Clerk,
  // a dev id or a demo session.
  const onMiniAppPath = typeof window !== "undefined" && isMiniAppPath(window.location.pathname);
  if (onMiniAppPath) {
    return <TelegramAuthProvider>{children}</TelegramAuthProvider>;
  }
  // #2824 - the demo phone's frame signs in as the picked staff member, first.
  if (inDemoPhoneFrame()) {
    return <DemoMemberAuthProvider>{children}</DemoMemberAuthProvider>;
  }
  const inner = demoSessionActive ? (
    <DemoSessionAuthProvider>{children}</DemoSessionAuthProvider>
  ) : devShimIdPresent && sessionEnablesDevShim ? (
    <DevShimAuthProvider>{children}</DevShimAuthProvider>
  ) : hasClerk ? (
    <ClerkAuthProvider>{children}</ClerkAuthProvider>
  ) : (
    <DevShimAuthProvider>{children}</DevShimAuthProvider>
  );
  return <DevShimSessionGuard>{inner}</DevShimSessionGuard>;
}

// ─── DevShimSessionGuard (issue 030) ─────────────────────────────────
//
// Brief 46's sessionStorage authorization flag was tab-scoped (good for
// issue 002) but session-wide (bad — it stayed `'1'` for the whole tab
// life). After a demo-pick, `resolveInitialWorkspace` redirects from `/`
// to `/teams/<demo-team-id>`, so the demo flow lives mostly OFF `/demo*`
// — a literal "clear flag when off /demo*" reading would break Brief 46.
//
// The real discriminator without a backend round-trip: at pick time the
// picker stores `scenario.team_id` in sessionStorage as
// `tarii.dev_shim_demo_team_id`. This guard watches React Router's
// pathname; when the URL hits `/teams/<id>` with id != stored demo
// team_id (i.e., the user has navigated to a non-demo team), it clears
// the flag + the demo team_id + the localStorage dev id, then reloads —
// AuthProvider re-mounts in Clerk mode for the user's real account.
//
// Mounted INSIDE BrowserRouter (see App.tsx — AuthProvider is wrapped
// by BrowserRouter), so `useLocation` is safe here.
function DevShimSessionGuard({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  useEffect(() => {
    if (typeof window === "undefined") return;
    const flag = window.sessionStorage.getItem("tarii.dev_shim_session_authorized");
    if (flag !== "1") return;
    const demoTeamId = window.sessionStorage.getItem("tarii.dev_shim_demo_team_id");
    const teamMatch = pathname.match(/^\/teams\/([^/]+)/);
    if (teamMatch && demoTeamId && teamMatch[1] !== demoTeamId) {
      window.sessionStorage.removeItem("tarii.dev_shim_session_authorized");
      window.sessionStorage.removeItem("tarii.dev_shim_demo_team_id");
      window.localStorage.removeItem("roster.dev_account_id");
      window.location.reload();
    }
  }, [pathname]);
  return <>{children}</>;
}

// ─── Ephemeral demo-session provider (#1753) ──────────────────────────
//
// Backs the `/demo` ephemeral surface: `POST /api/demo-sessions` minted
// a throwaway account + team and an opaque bearer token, stored via
// `lib/demoSession`. This provider sends `Bearer demo-session:<token>`
// on every request; the backend resolves it through the normal auth
// path, so the whole app works unmodified — the account simply has one
// (ephemeral) team. On 401/410 (expired or swept server-side) we clear
// the stored session and drop to signed-out, which paints the landing
// page / `/demo` entry rather than looping on a dead token.
// #2824 - mark this tab as a demo tab while a demo provider is mounted. Set in
// render so the children's first render already sees it; the effect re-sets it
// after a StrictMode remount and clears it on unmount.
function useMarkDemoTab(): void {
  setDemoTab(true);
  useEffect(() => {
    setDemoTab(true);
    return () => setDemoTab(false);
  }, []);
}

function DemoSessionAuthProvider({ children }: { children: ReactNode }) {
  useMarkDemoTab();
  const ended = useDemoEnded();
  const initialSession = typeof window !== "undefined" ? readDemoSession() : null;
  const [session, setSession] = useState(initialSession);
  const [account, setAccount] = useState<AccountMe | null>(null);
  const [loading, setLoading] = useState<boolean>(Boolean(initialSession));
  const [error, setError] = useState<string | null>(null);

  const fetchMe = useCallback(async (token: string, opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    setError(null);
    try {
      const me = await apiFetch<AccountMe>("/api/account/me", {
        headers: { Authorization: demoSessionAuthValue(token) },
      });
      setAccount(me);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load account";
      if (err instanceof ApiError && (err.status === 401 || err.status === 404)) {
        // The session expired (2 h TTL) or its rows were swept — the
        // token is dead. Clear it so the next mount is signed-out, and
        // record the end so the gate sends the visitor to /demo (#2986).
        endDemoSession();
        setSession(null);
      }
      setError(message);
      setAccount(null);
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (session) {
      void fetchMe(session.token);
    } else {
      setLoading(false);
    }
    // Mount-only: the session token never changes within one mount —
    // minting a new session hard-reloads into a fresh provider.
  }, []);

  // Central TokenSource registration — mirrors the sibling providers.
  const sessionRef = useRef(session);
  sessionRef.current = session;
  useEffect(() => {
    setTokenSource(async () => {
      const current = sessionRef.current;
      return current ? demoSessionAuthValue(current.token) : null;
    });
    return () => setTokenSource(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      accountId: session?.account_id ?? null,
      account,
      loading,
      error,
      mode: "demo",
      signIn: () => {
        // No-op — demo sessions are minted on `/demo`, never signed into.
      },
      signOut: async () => {
        // #2919 — the next account must not see this one's cached data.
        await clearAccountCaches();
        setActiveWorkspaceId(null);
        clearDemoSession();
        clearDemoEnded();
        setSession(null);
        setAccount(null);
      },
      refresh: async (opts) => {
        const current = sessionRef.current;
        if (current) await fetchMe(current.token, opts);
      },
      authHeader: async () => {
        const current = sessionRef.current;
        return current ? { Authorization: demoSessionAuthValue(current.token) } : {};
      },
    }),
    [session, account, loading, error, fetchMe],
  );

  return (
    <AuthContext.Provider value={value}>
      {ended ? <DemoEndedScreen /> : children}
    </AuthContext.Provider>
  );
}

// ─── Demo phone provider (#2824) ──────────────────────────────────────
//
// Mounted on `/demo-phone` only: the frame inside the demo roster page's
// phone. The address names the staff member (`?m=<membership id>`). Once, at
// mount, the visitor's own demo token (shared storage) buys a read-only
// `demo-member:` token for that person. After that only the new token is
// sent, from module memory: the frame never sends the lead's token again and
// never writes the lead's storage. The token is re-minted 5 minutes before it
// ends. A write the member app attempts gets the API's 403 note.
const DEMO_MEMBER_REMINT_LEAD_MS = 5 * 60 * 1000;

function DemoMemberAuthProvider({ children }: { children: ReactNode }) {
  useMarkDemoTab();
  const ended = useDemoEnded();
  const [account, setAccount] = useState<AccountMe | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const tokenRef = useRef<string | null>(null);
  const timerRef = useRef<number | null>(null);

  const headerValue = () => (tokenRef.current ? demoMemberAuthValue(tokenRef.current) : null);

  const mint = useCallback(async (): Promise<boolean> => {
    const membershipId = new URLSearchParams(window.location.search).get(DEMO_PHONE_MEMBER_PARAM);
    const lead = readDemoSession();
    if (!membershipId || !lead) return false;
    // #2824 - one sign-in per person for the whole demo: a frame that opens again
    // (or a person picked again) reuses the token until it is close to its end.
    const cacheKey = `tarii.demo_phone_token:${lead.token}:${membershipId}`;
    let session: MemberViewSession | null = null;
    try {
      const raw = window.sessionStorage.getItem(cacheKey);
      const cached = raw ? (JSON.parse(raw) as MemberViewSession) : null;
      if (cached && Date.parse(cached.expires_at) - Date.now() > DEMO_MEMBER_REMINT_LEAD_MS * 2) {
        session = cached;
      }
    } catch {
      session = null;
    }
    if (!session) {
      session = await mintMemberView(demoSessionAuthValue(lead.token), membershipId);
      try {
        window.sessionStorage.setItem(cacheKey, JSON.stringify(session));
      } catch {
        // Storage blocked: the next frame signs in again.
      }
    }
    tokenRef.current = session.token;
    const left = Date.parse(session.expires_at) - Date.now();
    const wait = Math.max(left - DEMO_MEMBER_REMINT_LEAD_MS, left / 2, 1000);
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      void mint().catch(() => {
        // A failed re-mint ends the phone: drop the token so no call reuses it.
        tokenRef.current = null;
        setAccount(null);
        setError("The phone session ended.");
      });
    }, wait);
    return true;
  }, []);

  const fetchMe = useCallback(async (opts?: { silent?: boolean }) => {
    const header = headerValue();
    if (!header) return;
    if (!opts?.silent) setLoading(true);
    try {
      setAccount(
        await apiFetch<AccountMe>("/api/account/me", { headers: { Authorization: header } }),
      );
      setError(null);
    } catch (err) {
      setAccount(null);
      setError(err instanceof Error ? err.message : "failed");
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    // The getter reads the ref, so it is right from the moment the swap lands.
    setTokenSource(async () => headerValue());
    let cancelled = false;
    void (async () => {
      try {
        const ok = await mint();
        if (cancelled) return;
        if (!ok) {
          setError("no_session");
          setLoading(false);
          return;
        }
        await fetchMe();
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "failed");
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      setTokenSource(null);
    };
  }, [mint, fetchMe]);

  const value = useMemo<AuthContextValue>(
    () => ({
      accountId: account?.id ?? null,
      account,
      loading,
      error,
      mode: "demo-member",
      signIn: () => {
        // No-op - the phone is signed in by the demo page.
      },
      signOut: async () => {
        // Nothing to end: the frame holds a token in memory only.
      },
      refresh: fetchMe,
      authHeader: async () => {
        const header = headerValue();
        return header ? { Authorization: header } : {};
      },
    }),
    [account, loading, error, fetchMe],
  );

  return (
    <AuthContext.Provider value={value}>
      {ended ? <DemoEndedScreen /> : children}
    </AuthContext.Provider>
  );
}

// ─── Telegram Mini App provider (#2074) ───────────────────────────────
//
// Mounted on `/tg` only. Loads Telegram's SDK, tells Telegram the page is
// ready, posts the signed `initData` to `/api/telegram/miniapp/session` and
// keeps the returned `tgapp` token in module memory (`telegram/session.ts`)
// — never in storage. `error` carries why there is no session
// (`not_linked`, `telegram_off`, …) so `<TelegramMiniApp/>` can say so in
// one line, or show the Connect steps.
function TelegramAuthProvider({ children }: { children: ReactNode }) {
  const [account, setAccount] = useState<AccountMe | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<MiniAppSessionProblem | string | null>(null);

  const fetchMe = useCallback(async (opts?: { silent?: boolean }) => {
    const header = tgappAuthValue();
    if (!header) return;
    if (!opts?.silent) setLoading(true);
    try {
      setAccount(
        await apiFetch<AccountMe>("/api/account/me", { headers: { Authorization: header } }),
      );
      setError(null);
    } catch (err) {
      setAccount(null);
      setError(err instanceof Error ? err.message : "failed");
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    // The getter reads the module ref, so it is right from the moment the
    // session lands — before any child mounts and fetches.
    setTokenSource(async () => tgappAuthValue());
    let cancelled = false;
    let cancelLate: (() => void) | null = null;
    void (async () => {
      const app = await loadTelegramSdk();
      if (cancelled) return;
      // A script slower than the wait still gets ready / expand / swipes off
      // when it lands (#2172 M2) — the session goes ahead without it.
      if (app) startTelegramChrome(app);
      else cancelLate = whenTelegramWebApp(startTelegramChrome);
      const result = await startMiniAppSession(readInitData(app));
      if (cancelled) return;
      if (result.kind === "problem") {
        setError(result.problem);
        setLoading(false);
        return;
      }
      await fetchMe();
    })();
    return () => {
      cancelled = true;
      cancelLate?.();
      setTokenSource(null);
    };
  }, [fetchMe]);

  const value = useMemo<AuthContextValue>(
    () => ({
      accountId: account?.id ?? null,
      account,
      loading,
      error,
      mode: "telegram",
      signIn: () => {
        // No-op — Telegram is the sign-in.
      },
      signOut: async () => {
        // Telegram is the session: nothing to revoke but the token in memory.
        // #2919 — drop this account's cached data first.
        await clearAccountCaches();
        setTgappToken(null);
        setAccount(null);
      },
      refresh: fetchMe,
      authHeader: async () => {
        const header = tgappAuthValue();
        return header ? { Authorization: header } : {};
      },
    }),
    [account, loading, error, fetchMe],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ─── Dev-shim provider (Stream A) ─────────────────────────────────────

/** #3005 — the dev shim lists recent dev ids in dev builds only, never in a demo. */
function devAccountListOn(): boolean {
  return import.meta.env.DEV && !isDemoMode();
}

function DevShimAuthProvider({ children }: { children: ReactNode }) {
  // Issue 007 — second-line dormancy guard. The AuthProvider gate above
  // already routes to <ClerkAuthProvider/> (or to this provider with the
  // expectation that it resolve to no-account) when not on `/demo*`. We
  // double-up here so that a DevShim mounted off-`/demo` always boots
  // with `accountId=null`, which lets `RequireAuthOrLanding` paint the
  // LandingPage / bounce to /sign-in regardless of what's in localStorage.
  //
  // Issue 024 — the dormancy guard is widened in lockstep with the outer
  // gate: a session that has the `tarii.dev_shim_session_authorized` flag
  // is a deliberate-this-session demo session, so the localStorage id is
  // legitimate even off `/demo*`. The flag is tab-scoped (sessionStorage
  // clears on tab close), so issue 002's stale-localStorage scenario
  // still yields `accountId=null` because no flag is present.
  const initialId =
    typeof window !== "undefined" &&
    (isDemoPath(window.location.pathname) ||
      window.sessionStorage.getItem("tarii.dev_shim_session_authorized") === "1")
      ? readDevAccountId()
      : null;
  const [accountId, setAccountId] = useState<string | null>(initialId);
  const [account, setAccount] = useState<AccountMe | null>(null);
  const [loading, setLoading] = useState<boolean>(Boolean(accountId));
  const [error, setError] = useState<string | null>(null);
  const [recent, setRecent] = useState(() => readDevRecentAccounts());

  const fetchMe = useCallback(async (id: string, opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    setError(null);
    try {
      const me = await apiFetch<AccountMe>("/api/account/me", {
        headers: authHeaders(id),
      });
      setAccount(me);
      // #3005 — dev builds remember each dev id used here, for Switch accounts.
      if (devAccountListOn()) {
        noteDevRecentAccount({
          id: me.id,
          name: me.display_name || me.email || me.id,
          email: me.email ?? null,
        });
        setRecent(readDevRecentAccounts());
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load account";
      // Stale-localStorage recovery: a 401 / 404 on `/api/account/me`
      // with a dev-shim id means the stored Account row no longer
      // exists — typically because a backend reseed regenerated the
      // synthetic per-doctor Account UUIDs (pre-fix-A from this same
      // PR; legacy random uuid4 in seed.py shadow_account creation).
      // Without this branch, the SPA stays "signed in" with an id that
      // 401's on every call, manifesting as "demo team can no longer
      // be found". Clear the stale id so the next mount falls through
      // to the LandingPage / `/demo` flow instead of looping on a dead
      // session.
      if (err instanceof ApiError && (err.status === 401 || err.status === 404)) {
        // #2919 — the dead account's cached data goes with it.
        void clearAccountCaches();
        clearDevAccountId();
        if (typeof window !== "undefined") {
          window.sessionStorage.removeItem("tarii.dev_shim_session_authorized");
          window.sessionStorage.removeItem("tarii.dev_shim_demo_team_id");
          window.sessionStorage.removeItem("tarii.demo_scenario");
        }
        setAccountId(null);
      }
      setError(message);
      setAccount(null);
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    return subscribeDevAccountId((next) => {
      setAccountId(next);
      if (next) {
        void fetchMe(next);
      } else {
        setAccount(null);
        setLoading(false);
      }
    });
  }, [fetchMe]);

  useEffect(() => {
    if (accountId) {
      void fetchMe(accountId);
    } else {
      setLoading(false);
    }
    // Mount-only — subscribeDevAccountId handles subsequent changes.
  }, []);

  // ─── Central TokenSource registration (#773) ──────────────────────
  //
  // Register a module-level getter so `api/client.ts` `apiFetch` can
  // inject the dev-shim `Authorization` header from non-React contexts
  // (Zustand stores, the SSE stream) without each call-site threading a
  // header by hand. This is now the only auth path: call-sites no longer
  // pass explicit headers, so `apiFetch` fills auth for essentially every
  // request from this source. The getter reads the LATEST accountId via
  // a ref kept current on every render — so it never captures a stale
  // closure even though it's registered ONCE on mount. (Registering
  // once + reading the ref is cleaner than re-registering on every
  // `accountId` change and is equivalent: callers always observe the
  // current id.) We clear the source on unmount so a swap to the Clerk
  // provider can't leave a dead dev-shim getter behind.
  //
  // The returned value mirrors `authHeaders(accountId)` exactly — the
  // `Authorization` value `Bearer dev:<accountId>`, or `null` when
  // there's no accountId. `apiFetch` only consults this when the caller
  // did NOT already set `Authorization`; the sole remaining explicit-
  // header caller is this provider's own `/api/account/me` bootstrap.
  const accountIdRef = useRef<string | null>(accountId);
  accountIdRef.current = accountId;
  useEffect(() => {
    setTokenSource(async () => {
      const id = accountIdRef.current;
      return id ? `Bearer dev:${id}` : null;
    });
    return () => setTokenSource(null);
  }, []);

  // #3005 — the dev ids this browser used, current first (dev builds, not a demo).
  const listOn = devAccountListOn();
  const accounts = useMemo<SignedInAccount[] | undefined>(() => {
    if (!listOn) return undefined;
    const rows = recent.map((r) => ({
      key: r.id,
      name: r.name,
      email: r.email,
      imageUrl: null,
      current: r.id === accountId,
    }));
    return [...rows.filter((r) => r.current), ...rows.filter((r) => !r.current)];
  }, [listOn, recent, accountId]);

  const value = useMemo<AuthContextValue>(
    () => ({
      accountId,
      account,
      loading,
      error,
      mode: "dev-shim",
      signIn: (id: string) => setDevAccountId(id),
      // No server-side session to revoke — clear the local id and resolve
      // immediately so callers can `await signOut()` against either provider.
      signOut: async (opts) => {
        // #3005 — this account leaves the list; `all` empties it.
        if (opts?.scope === "all") clearDevRecentAccounts();
        else if (accountId) forgetDevRecentAccount(accountId);
        // #1661 — the workspace pointer is account-agnostic storage; a
        // new sign-in must not inherit the previous account's workspace.
        // #2919 — nor its cached data (worker cache + react-query).
        await clearAccountCaches();
        setActiveWorkspaceId(null);
        clearDevAccountId();
      },
      ...(accounts
        ? {
            accounts,
            switchAccount: (id: string) => {
              // Fresh from storage: the add picker records the row first.
              const row = readDevRecentAccounts().find((r) => r.id === id);
              return runAccountSwitch({
                label: row?.name ?? id,
                announceAs: row?.email ?? row?.name ?? id,
                keepDevId: true,
                activate: () => activateDevAccount(id, setDevAccountId),
              });
            },
          }
        : {}),
      refresh: async (opts) => {
        if (accountId) await fetchMe(accountId, opts);
      },
      authHeader: async () => authHeaders(accountId),
    }),
    [accountId, account, loading, error, fetchMe, accounts],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ─── Clerk provider (Stream C) ────────────────────────────────────────

/**
 * #2240 — waits between attempts at `/api/account/me` on a (non-silent)
 * load. A tab back from a long idle can fail its first attempt for
 * reasons that clear in a moment: Clerk is still minting a fresh token
 * (or hands back one the API now refuses — the retry asks Clerk to skip
 * its cache), or the API is briefly unreachable. Exported for tests.
 */
export const ACCOUNT_RETRY_DELAYS_MS: number[] = [1000, 3000];

/** A failure worth another attempt: no response, a refused token, or a server-side hiccup. */
function worthRetrying(err: unknown): boolean {
  if (!(err instanceof ApiError)) return true;
  return err.status === 401 || err.status === 408 || err.status === 429 || err.status >= 500;
}

type ClerkSession = NonNullable<ReturnType<typeof useSessionList>["sessions"]>[number];

/** #3005 — one Clerk session as a row of the Switch accounts list. */
function sessionToAccount(
  session: ClerkSession,
  currentId: string | null,
  names: Record<string, string>,
): SignedInAccount {
  const user = session.user;
  const pub = session.publicUserData;
  const email = user?.primaryEmailAddress?.emailAddress ?? pub?.identifier ?? null;
  const clerkName =
    user?.fullName || [pub?.firstName, pub?.lastName].filter(Boolean).join(" ") || null;
  const hasImage = user ? user.hasImage : Boolean(pub?.hasImage);
  return {
    key: session.id,
    name: (user && names[user.id]) || clerkName || email || "Account",
    email,
    imageUrl: hasImage ? (user?.imageUrl ?? pub?.imageUrl ?? null) : null,
    current: session.id === currentId,
  };
}

function ClerkAuthProvider({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn, sessionId, signOut: clerkSignOut, getToken } = useClerkAuth();
  // #3005 — Clerk's signed-in sessions on this browser (multi-session).
  const sessionList = useSessionList();
  const setActive = sessionList.setActive;
  const [names, setNames] = useState<Record<string, string>>(() => readAccountNames());
  const [account, setAccount] = useState<AccountMe | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  // Only the latest account request may write its result — a slow attempt
  // from before a sign-in flip must not overwrite a newer one.
  const fetchGeneration = useRef(0);
  // The loaded account, readable inside `fetchMe` without re-creating it.
  const accountRef = useRef<AccountMe | null>(account);
  accountRef.current = account;

  // #2919 — clear the account caches each time Clerk reports signed out
  // (the first load signed out, a sign-out in another tab, an expired
  // session, or the end of our own sign-out). It also catches a refill
  // between our pre-revoke clear and the revoke landing.
  const signedOutCleared = useRef(false);
  useEffect(() => {
    if (!isLoaded) return;
    if (isSignedIn) {
      signedOutCleared.current = false;
      return;
    }
    if (signedOutCleared.current) return;
    signedOutCleared.current = true;
    void clearAccountCaches();
  }, [isLoaded, isSignedIn]);

  // #3005 — the list: signed-in sessions only (founder D1 a), the
  // current one first. A session still finishing a task is left out.
  const currentSessionId = sessionId ?? null;
  const sessions = sessionList.isLoaded ? sessionList.sessions : undefined;
  const accounts = useMemo<SignedInAccount[]>(() => {
    const rows = (sessions ?? [])
      .filter((s) => s.status === "active")
      .map((s) => sessionToAccount(s, currentSessionId, names));
    return [...rows.filter((r) => r.current), ...rows.filter((r) => !r.current)];
  }, [sessions, currentSessionId, names]);
  const accountsRef = useRef(accounts);
  accountsRef.current = accounts;
  const currentUserId =
    sessions?.find((s) => s.id === currentSessionId && s.status === "active")?.user?.id ?? null;
  const signedInUserIds = (sessions ?? [])
    .filter((s) => s.status === "active")
    .map((s) => s.user?.id)
    .filter((id): id is string => Boolean(id))
    .join(",");

  // The current account's Tarii name, for the list in later loads.
  useEffect(() => {
    if (!account || !currentUserId) return;
    const name = account.display_name || account.email;
    if (!name) return;
    rememberAccountName(currentUserId, name);
    setNames(readAccountNames());
  }, [account, currentUserId]);

  // #3005 — Add another account with Google comes back through
  // `/sso-callback`, where no switch of ours runs. Once an account loads
  // under another session than the one the add left from, tell the other
  // tabs, so they follow (D2 a).
  useEffect(() => {
    if (!account || !currentSessionId) return;
    if (!takeAccountAddPending(currentSessionId)) return;
    announceAccountSwitch(account.email || account.display_name || "another account");
  }, [account, currentSessionId]);

  // D1 a — a signed-out account leaves no name behind on this browser.
  useEffect(() => {
    if (!sessionList.isLoaded) return;
    forgetAccountNames(signedInUserIds ? signedInUserIds.split(",") : []);
  }, [sessionList.isLoaded, signedInUserIds]);

  // #3005 — watch the session id. Clerk's `getToken` keeps one identity
  // per Clerk instance in clerk-react, and the account below loads again
  // only when `isSignedIn` flips. So a change of session our code did not
  // start (another tab, Clerk itself) follows with a clear and a full
  // load of `/` at the next safe moment (D2 a). The current session
  // ending while others live opens Choose an account.
  const signingOut = useRef(false);
  const lastSessionId = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (!isLoaded) return;
    const previous = lastSessionId.current;
    lastSessionId.current = currentSessionId;
    if (previous === undefined || previous === currentSessionId) return;
    if (isAccountSwitching() || signingOut.current) return;
    if (previous !== null && currentSessionId !== null) {
      const next = accountsRef.current.find((a) => a.key === currentSessionId);
      followAccountSwitch(next?.email ?? next?.name ?? "another account");
      return;
    }
    if (
      previous !== null &&
      currentSessionId === null &&
      accountsRef.current.length > 0 &&
      window.location.pathname !== CHOOSE_ACCOUNT_PATH
    ) {
      window.location.assign(CHOOSE_ACCOUNT_PATH);
    }
  }, [isLoaded, currentSessionId]);

  const buildHeader = useCallback(
    async (opts?: { fresh?: boolean }): Promise<Record<string, string>> => {
      if (!isSignedIn) return {};
      const token = await getToken(opts?.fresh ? { skipCache: true } : undefined);
      return token ? { Authorization: `Bearer ${token}` } : {};
    },
    [isSignedIn, getToken],
  );

  const fetchMe = useCallback(
    async (opts?: { silent?: boolean }) => {
      const generation = ++fetchGeneration.current;
      if (!isSignedIn) {
        setAccount(null);
        setError(null);
        setLoading(false);
        return;
      }
      // A silent refresh changes nothing on screen until it has an answer
      // (clearing `error` here would turn a failed state back into loading).
      if (!opts?.silent) {
        setLoading(true);
        setError(null);
      }
      const delays = opts?.silent ? [] : ACCOUNT_RETRY_DELAYS_MS;
      try {
        for (let attempt = 0; ; attempt++) {
          try {
            const headers = await buildHeader({ fresh: attempt > 0 });
            const me = await apiFetch<AccountMe>("/api/account/me", { headers });
            if (generation === fetchGeneration.current) {
              setAccount(me);
              setError(null);
            }
            return;
          } catch (err) {
            const delay = delays[attempt];
            if (delay === undefined || !worthRetrying(err)) throw err;
            await new Promise((resolve) => setTimeout(resolve, delay));
            if (generation !== fetchGeneration.current) return;
          }
        }
      } catch (err) {
        if (generation !== fetchGeneration.current) return;
        // #2240 — a re-fetch that fails mid-session (SolvePage's and the
        // profile page's silent refresh, or any refresh once an account is
        // loaded) keeps the account it had: swapping the whole app for
        // "We couldn't load your account" over one failed read is worse
        // than a moment of stale plan or name. A failed silent refresh
        // with no account leaves the state it found.
        if (accountRef.current !== null || opts?.silent) return;
        const message = err instanceof Error ? err.message : "Failed to load account";
        setError(message);
        setAccount(null);
      } finally {
        if (!opts?.silent) setLoading(false);
      }
    },
    [isSignedIn, buildHeader],
  );

  useEffect(() => {
    if (!isLoaded) return;
    void fetchMe();
    // `fetchMe` already closes over `isSignedIn` (its own dep), so listing
    // it here too is redundant — the effect re-runs when sign-in flips
    // because `fetchMe`'s identity changes.
  }, [isLoaded, fetchMe]);

  // ─── Central TokenSource registration (#773, Phase 0) ──────────────
  //
  // Mirror the dev-shim provider: register a module-level getter so
  // `apiFetch` can inject the real Clerk JWT from non-React contexts.
  // The getter reuses the existing `buildHeader` (the same logic
  // `authHeader()` exposes — mints a fresh JWT via Clerk `getToken()`)
  // and extracts the `Authorization` string from it, so the central
  // source returns the SAME header `authHeader()` builds: `Bearer
  // <jwt>` when signed in, else `null`.
  //
  // We read `buildHeader` through a ref kept current on every render, so
  // the registered getter always calls the freshest closure (re-mints
  // per call → survives Clerk's ~60s dev-tenant JWT rotation, per the
  // E2E TokenSource pattern) even though it's registered ONCE on mount.
  // A sign-in flip / `getToken` identity change updates the ref on the
  // next render, so the getter can never go stale. Clear on unmount.
  const buildHeaderRef = useRef(buildHeader);
  buildHeaderRef.current = buildHeader;
  useEffect(() => {
    setTokenSource(async () => {
      const header = await buildHeaderRef.current();
      return header.Authorization ?? null;
    });
    return () => setTokenSource(null);
  }, []);

  // #2240 — signed in to Clerk but no account yet and no failure either:
  // the account request is still to come (the render between Clerk
  // loading and the fetch effect, or a sign-in flip) or in flight. That
  // is loading, never "signed out" — the gates must not bounce it.
  const signedIn = isLoaded && Boolean(isSignedIn);
  const accountPending = signedIn && account === null && error === null;

  const value = useMemo<AuthContextValue>(
    () => ({
      accountId: account?.id ?? null,
      account,
      loading: !isLoaded || loading || accountPending,
      error,
      sessionWithoutAccount: signedIn && account === null && error !== null,
      mode: "clerk",
      signIn: () => {
        // No-op in Clerk mode — sign-in flows go through Clerk's
        // hosted components on `/sign-in`.
      },
      // Return the promise — never `void` it. Clerk's `signOut()` is a
      // network round-trip that revokes the session and clears the cookie;
      // discarding the promise let callers navigate away mid-flight, which
      // aborted the revoke and left the user signed in after the reload
      // (#1520). Clerk owns the post-revoke redirect, so it happens only
      // once the request has actually landed.
      signOut: (opts) => {
        // #1661 — clear the account-agnostic workspace pointer BEFORE the
        // revoke round-trip so the next sign-in starts from its own
        // workspace resolution, never the previous account's.
        setActiveWorkspaceId(null);
        // #3005 — `current` (the default) ends this session only; with
        // other accounts signed in, Clerk's `signOut()` with no session id
        // would end every one. Our own sign-out is not a change to follow.
        const all = opts?.scope === "all";
        signingOut.current = true;
        if (all) forgetAccountNames();
        // #2919 — drop this account's cached data (worker cache +
        // react-query) before the revoke; it resolves within about 1 s.
        return clearAccountCaches().then(() =>
          clerkSignOut({
            redirectUrl: opts?.redirectUrl ?? "/",
            ...(!all && currentSessionId ? { sessionId: currentSessionId } : {}),
          }),
        );
      },
      accounts,
      switchAccount: async (key: string) => {
        const target = accountsRef.current.find((a) => a.key === key);
        if (!target || target.current || !setActive) return;
        await runAccountSwitch({
          label: target.name,
          // Other tabs say "Now signed in as ‹email›".
          announceAs: target.email ?? target.name,
          activate: () => setActive({ session: key }),
        });
      },
      refresh: fetchMe,
      authHeader: () => buildHeader(),
    }),
    [
      account,
      isLoaded,
      loading,
      accountPending,
      signedIn,
      error,
      clerkSignOut,
      fetchMe,
      buildHeader,
      accounts,
      currentSessionId,
      setActive,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** True in a demo session. Safe outside an `<AuthProvider>` (reads false). */
export function useIsDemoSession(): boolean {
  return useContext(AuthContext)?.mode === "demo";
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}
